/**
 * Config Tab Component
 * Displays Equifax configuration in a card section with an iframe
 */

import { useState, useRef, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { useAppSelector } from '@/app/hooks'
import { selectToken } from '@/features/auth/authSlice'
import { getBrowserApiBaseUrl } from '@config/env'
import { showError } from '@/lib/utils/notifications'
import {
  MODULE_LOADING_SPINNER_CLASS,
  ModuleScreenLoading,
} from '@/components/ui/ModuleScreenLoading'
import { SectionCard } from '@/components/ui/SectionCard'
import type { TabProps } from '../../types/underwritingConfig'

const IFRAME_DEFAULT_HEIGHT = '1250px'

export function ConfigTab({ isLoading }: TabProps) {
  const token = useAppSelector(selectToken)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [iframeUrl, setIframeUrl] = useState<string>('')
  const [iframeLoading, setIframeLoading] = useState(true)

  /**
   * Build iframe URL with auth token
   * Note: Token in URL is a security consideration - ideally this would use auth header
   * TODO: Coordinate with backend to support Authorization header instead of query param
   */
  useEffect(() => {
    if (token) {
      const base = getBrowserApiBaseUrl()
      const url = `${base}/api/equifax/config?access_token=${encodeURIComponent(token)}`
      setIframeUrl(url)
    } else {
      showError('Authentication token not found')
    }
  }, [token])

  /**
   * Auto-resize iframe based on content height
   * Matches Angular implementation: set initial height, then adjust on load
   */
  const handleIframeLoad = () => {
    try {
      if (iframeRef.current?.contentWindow?.document?.body?.scrollHeight) {
        const height = iframeRef.current.contentWindow.document.body.scrollHeight
        iframeRef.current.style.height = `${Math.max(height, 400)}px`
      }
    } catch {
      // CORS may prevent height calculation — iframe keeps IFRAME_DEFAULT_HEIGHT.
    } finally {
      setIframeLoading(false)
    }
  }

  if (isLoading) {
    return <ModuleScreenLoading minHeight="300px" />
  }

  return (
    <div className="w-full space-y-6">
      <SectionCard testId="underwriting-config-equifax-card">
        <div className="relative min-h-[320px] w-full overflow-hidden rounded-lg border border-slate-200 bg-white">
          {iframeLoading && (
            <div className="absolute inset-0 z-10 flex min-h-[320px] items-center justify-center bg-white/85">
              <Loader2 className={MODULE_LOADING_SPINNER_CLASS} aria-hidden />
            </div>
          )}

          {iframeUrl && (
            <iframe
              ref={iframeRef}
              title="Equifax Configuration"
              src={iframeUrl}
              className="block w-full border-0 bg-white"
              style={{
                height: IFRAME_DEFAULT_HEIGHT,
                visibility: iframeLoading ? 'hidden' : 'visible',
              }}
              onLoad={handleIframeLoad}
            />
          )}
        </div>
      </SectionCard>
    </div>
  )
}
